import { createAsyncThunk } from '@reduxjs/toolkit'

import { PLAN_URLS } from '../../../../configs/api'
import { REQUEST_OPTIONS } from '../../../../constants/request-options'
import { AxiosService } from '../../../../services/api'
import { createThunkName } from '../../../../utils/createThunkname'
import { saveData } from '../../../../utils/downloadFile'
import {
  AddPlanDto,
  DeletePlanByIdDto,
  ExportPlansQueryParams,
  GetAllPlansQueryParams,
  GetAllPlansResponse,
  GetCountryIdListFromExcelResponse,
  GetPlanCountriesResponse,
  GetPlanDetailResponse,
  GetPlanExceptionResponse,
  GetPlanInfoResponse,
  GetPlanPricesResponse,
  GetPlanSpecialLabelResponse,
  GetPlanStatusResponse,
  GetPlanTimestampResponse,
  GetPlanTypeResponse,
  GetPriceListFromExcelResponse,
  UpdatePlanCountriesDto,
  UpdatePlanDetailDto,
  UpdatePlanExceptionDto,
  UpdatePlanInfoDto,
  UpdatePlanPricesDto,
  UpdatePlanSpecialLabelDto,
  UpdatePlanStatusDto,
  UpdatePlanTypeDto
} from './types'

const PREFIX = 'Plan'

const getPlanById = {
  getPlanStatusAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanStatus'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanStatusResponse>(PLAN_URLS.getPlanStatus(planId))
      return result.data.isActive
    } catch (error) {
      throw error
    }
  }),
  getPlanInfoAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanInfo'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanInfoResponse>(PLAN_URLS.getPlanInfo(planId))
      return result.data.plan
    } catch (error) {
      throw error
    }
  }),
  getPlanTypeAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanType'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanTypeResponse>(PLAN_URLS.getPlanType(planId))
      return result.data.plan
    } catch (error) {
      throw error
    }
  }),
  getPlanCountriesAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanCountries'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanCountriesResponse>(PLAN_URLS.getPlanCountries(planId))
      return result.data.countryIds
    } catch (error) {
      throw error
    }
  }),
  getPlanPricesAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanPrices'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanPricesResponse>(PLAN_URLS.getPlanPrices(planId))
      return result.data
    } catch (error) {
      throw error
    }
  }),
  getPlanSpecialLabelAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getPlanSpecialLabel'),
    async (planId: string) => {
      try {
        const result = await AxiosService.get<GetPlanSpecialLabelResponse>(PLAN_URLS.getPlanSpecialLabel(planId))
        return result.data.specialLabel
      } catch (error) {
        throw error
      }
    }
  ),
  getPlanDetailAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanDetail'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanDetailResponse>(PLAN_URLS.getPlanDetail(planId))
      return result.data.plan
    } catch (error) {
      throw error
    }
  }),
  getPlanExceptionAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanException'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanExceptionResponse>(PLAN_URLS.getPlanException(planId))
      return result.data.exception
    } catch (error) {
      throw error
    }
  }),
  getPlanTimestampAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPlanTimestamp'), async (planId: string) => {
    try {
      const result = await AxiosService.get<GetPlanTimestampResponse>(PLAN_URLS.getPlanTimestamp(planId))
      return result.data.plan
    } catch (error) {
      throw error
    }
  })
}

const updatePlanById = {
  updatePlanStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanStatus'),
    async (dto: UpdatePlanStatusDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanStatus, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanInfoAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanInfo'),
    async (dto: UpdatePlanInfoDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanInfo, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanTypeAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanType'),
    async (dto: UpdatePlanTypeDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanType, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanCountriesAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanCountries'),
    async (dto: UpdatePlanCountriesDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanCountries, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanPricesAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanPrices'),
    async (dto: UpdatePlanPricesDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanPrices, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanSpecialLabelAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanSpecialLabel'),
    async (dto: UpdatePlanSpecialLabelDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanSpecialLabel, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanDetail'),
    async (dto: UpdatePlanDetailDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanDetail, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanExceptionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanException'),
    async (dto: UpdatePlanExceptionDto) => {
      try {
        await AxiosService.put(PLAN_URLS.updatePlanException, dto)
      } catch (error) {
        throw error
      }
    }
  )
}

export const PlanService = {
  getAllPlansAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllPlans'),
    async (queryParams: GetAllPlansQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllPlansResponse>(PLAN_URLS.getAllPlans, { params: queryParams })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  exportPlansAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportPlans'),
    async (queryParams: ExportPlansQueryParams) => {
      try {
        const result = await AxiosService.get(PLAN_URLS.exportPlans, {
          ...REQUEST_OPTIONS,
          params: queryParams
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  downloadPlanTemplateAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'downloadPlanTemplate'), async () => {
    try {
      const result = await AxiosService.get(PLAN_URLS.downloadPlanTemplate, {
        ...REQUEST_OPTIONS
      })
      saveData(result, result.request.getResponseHeader('Content-Disposition'))
    } catch (error) {
      throw error
    }
  }),
  importPlanTemplateAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'importPlanTemplate'), async (file: File) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      await AxiosService.post(PLAN_URLS.importPlanTemplate, formData)
    } catch (error) {
      throw error
    }
  }),
  addPlanAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'addPlan'), async (dto: AddPlanDto) => {
    try {
      await AxiosService.post(PLAN_URLS.addPlan, dto)
    } catch (error) {
      throw error
    }
  }),
  deletePlanByIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'deletePlanById'),
    async (dto: DeletePlanByIdDto) => {
      try {
        await AxiosService.post(PLAN_URLS.deletePlanById, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  downloadCountryTemplateAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'downloadCountryTemplate'), async () => {
    try {
      const result = await AxiosService.get(PLAN_URLS.downloadCountryTemplate, {
        ...REQUEST_OPTIONS
      })
      saveData(result, result.request.getResponseHeader('Content-Disposition'))
    } catch (error) {
      throw error
    }
  }),
  getCountryIdListFromExcelAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCountryIdListFromExcel'),
    async (file: File) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        const result = await AxiosService.post<GetCountryIdListFromExcelResponse>(
          PLAN_URLS.getCountryIdListFromExcel,
          formData
        )
        return result.data.countryIds
      } catch (error) {
        throw error
      }
    }
  ),
  exportPlanCountryByIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportPlanCountryById'),
    async (planId: string) => {
      try {
        const result = await AxiosService.get(PLAN_URLS.exportPlanCountryById(planId), {
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  downloadPriceTemplateAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'downloadPriceTemplate'), async () => {
    try {
      const result = await AxiosService.get(PLAN_URLS.downloadPriceTemplate, {
        ...REQUEST_OPTIONS
      })
      saveData(result, result.request.getResponseHeader('Content-Disposition'))
    } catch (error) {
      throw error
    }
  }),
  getPriceListFromExcelAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getPriceListFromExcel'),
    async (file: File) => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        const result = await AxiosService.post<GetPriceListFromExcelResponse>(PLAN_URLS.getPriceListFromExcel, formData)
        return result.data.prices
      } catch (error) {
        throw error
      }
    }
  ),
  exportPlanPriceByIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportPlanPriceById'),
    async (planId: string) => {
      try {
        const result = await AxiosService.get(PLAN_URLS.exportPlanPriceById(planId), {
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  ...getPlanById,
  ...updatePlanById
}
